import React, { useState } from 'react';
import {
  Box, Typography, Stack, Paper, Button, TextField, MenuItem,
  Select, FormControl, InputLabel, IconButton, Chip, Tooltip,
  Divider, Collapse,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  AccountBalance as BankIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  CheckCircle as ApprovedIcon, Cancel as DeclinedIcon,
  HourglassEmpty as PendingIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import salesAPI from '../../api/sales';

const currency = (n) => n != null ? `$${Number(n).toLocaleString()}` : '—';
const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  : '—';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', color: '#f59e0b' },
  { value: 'approved', label: 'Approved', color: '#10b981' },
  { value: 'declined', label: 'Declined', color: '#ef4444' },
];

const STATUS_COLORS = { pending: '#f59e0b', approved: '#10b981', declined: '#ef4444' };
const STATUS_ICONS = {
  pending: <PendingIcon fontSize="small" />,
  approved: <ApprovedIcon fontSize="small" />,
  declined: <DeclinedIcon fontSize="small" />,
};

const EMPTY_FORM = {
  bankName: '',
  dateSubmitted: new Date().toISOString().split('T')[0],
  status: 'pending',
  approvalAmount: '',
  approvalTerm: '',
  declineNotes: '',
};

export default function SubmissionsTab({ lead, onLeadUpdated }) {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const submissions = lead?.submissions || [];

  // Compute summary
  const totalSubmissions = submissions.length;
  const pendingCount = submissions.filter(s => s.status === 'pending').length;
  const approvedCount = submissions.filter(s => s.status === 'approved').length;
  const declinedCount = submissions.filter(s => s.status === 'declined').length;

  const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const resetForm = () => {
    setForm({ ...EMPTY_FORM, dateSubmitted: new Date().toISOString().split('T')[0] });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (sub) => {
    setForm({
      bankName: sub.bankName || '',
      dateSubmitted: sub.dateSubmitted ? new Date(sub.dateSubmitted).toISOString().split('T')[0] : '',
      status: sub.status || 'pending',
      approvalAmount: sub.approvalAmount || '',
      approvalTerm: sub.approvalTerm || '',
      declineNotes: sub.declineNotes || '',
    });
    setEditingId(sub._id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.bankName.trim()) {
      toast.warning('Bank name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        bankName: form.bankName.trim(),
        dateSubmitted: form.dateSubmitted || new Date().toISOString(),
        status: form.status,
        approvalAmount: form.status === 'approved' && form.approvalAmount ? parseFloat(form.approvalAmount) : undefined,
        approvalTerm: form.status === 'approved' ? form.approvalTerm : undefined,
        declineNotes: form.status === 'declined' ? form.declineNotes : undefined,
      };

      let res;
      if (editingId) {
        res = await salesAPI.updateSubmission(lead._id, editingId, payload);
        toast.success('Submission updated');
      } else {
        res = await salesAPI.addSubmission(lead._id, payload);
        toast.success('Submission added');
      }
      onLeadUpdated(res.data.data);
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save submission');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (submissionId) => {
    if (!window.confirm('Delete this submission?')) return;
    try {
      const res = await salesAPI.deleteSubmission(lead._id, submissionId);
      onLeadUpdated(res.data.data);
      toast.success('Submission deleted');
    } catch {
      toast.error('Failed to delete submission');
    }
  };

  return (
    <Box>
      {/* ── Summary Cards ──────────────────────────────────────────────────── */}
      <Stack direction="row" gap={1} mb={2} flexWrap="wrap">
        {[
          { label: 'Total', value: totalSubmissions, color: '#3b82f6' },
          { label: 'Pending', value: pendingCount, color: '#f59e0b' },
          { label: 'Approved', value: approvedCount, color: '#10b981' },
          { label: 'Declined', value: declinedCount, color: '#ef4444' },
        ].map((c) => (
          <Paper key={c.label} elevation={0} sx={{
            flex: 1, minWidth: 90, p: 1.25, textAlign: 'center',
            border: '1px solid', borderColor: 'divider', borderRadius: 2,
          }}>
            <Typography variant="h6" fontWeight={800} sx={{ color: c.color }}>{c.value}</Typography>
            <Typography variant="caption" color="text.secondary">{c.label}</Typography>
          </Paper>
        ))}
      </Stack>

      {/* ── Add Button ─────────────────────────────────────────────────────── */}
      <Button
        variant={showForm ? 'outlined' : 'contained'}
        startIcon={<AddIcon />}
        size="small"
        onClick={() => showForm ? resetForm() : setShowForm(true)}
        fullWidth
        sx={{ mb: 2 }}
      >
        {showForm ? 'Cancel' : 'Add Submission'}
      </Button>

      {/* ── Add / Edit Form ────────────────────────────────────────────────── */}
      <Collapse in={showForm}>
        <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'primary.light', borderRadius: 2, bgcolor: 'action.hover' }}>
          <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
            {editingId ? 'Edit Submission' : 'New Submission'}
          </Typography>

          <Stack gap={1.5}>
            <TextField label="Bank Name" fullWidth size="small" required
              value={form.bankName} onChange={handleChange('bankName')} />

            <TextField label="Date Submitted" type="date" fullWidth size="small"
              value={form.dateSubmitted} onChange={handleChange('dateSubmitted')}
              InputLabelProps={{ shrink: true }} />

            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={form.status} onChange={handleChange('status')}>
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    <Stack direction="row" gap={1} alignItems="center">
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color }} />
                      {s.label}
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Approved fields */}
            {form.status === 'approved' && (
              <>
                <TextField label="Approval Amount" type="number" fullWidth size="small"
                  value={form.approvalAmount} onChange={handleChange('approvalAmount')}
                  InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>$</Typography> }} />
                <TextField label="Term (e.g. 12 months, 6 months)" fullWidth size="small"
                  value={form.approvalTerm} onChange={handleChange('approvalTerm')} />
              </>
            )}

            {/* Declined fields */}
            {form.status === 'declined' && (
              <TextField label="Decline Notes / Reason" fullWidth size="small" multiline minRows={2}
                value={form.declineNotes} onChange={handleChange('declineNotes')} />
            )}
          </Stack>

          <Stack direction="row" gap={1} mt={2}>
            <Button variant="contained" size="small" onClick={handleSave} disabled={saving} fullWidth>
              {saving ? 'Saving…' : editingId ? 'Update' : 'Save Submission'}
            </Button>
            <Button variant="outlined" size="small" onClick={resetForm} sx={{ minWidth: 80 }}>Cancel</Button>
          </Stack>
        </Paper>
      </Collapse>

      {/* ── Submission Cards ───────────────────────────────────────────────── */}
      {submissions.length === 0 && !showForm && (
        <Typography color="text.secondary" textAlign="center" py={3}>
          No submissions yet. Click "Add Submission" to track bank submissions.
        </Typography>
      )}

      <Stack gap={1.5}>
        {submissions.map((sub) => {
          const isExpanded = expandedId === sub._id;
          const statusColor = STATUS_COLORS[sub.status] || '#64748b';

          return (
            <Paper key={sub._id} elevation={0} sx={{
              border: '1px solid', borderColor: 'divider', borderRadius: 2,
              overflow: 'hidden',
            }}>
              {/* Card header */}
              <Stack
                direction="row" alignItems="center" gap={1} sx={{
                  p: 1.5, cursor: 'pointer',
                  borderLeft: `4px solid ${statusColor}`,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
                onClick={() => setExpandedId(isExpanded ? null : sub._id)}
              >
                <BankIcon sx={{ color: statusColor, fontSize: 20 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700} noWrap>{sub.bankName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fmtDate(sub.dateSubmitted)}
                  </Typography>
                </Box>
                <Chip
                  icon={STATUS_ICONS[sub.status]}
                  label={sub.status.charAt(0).toUpperCase() + sub.status.slice(1)}
                  size="small"
                  sx={{
                    bgcolor: `${statusColor}18`,
                    color: statusColor,
                    fontWeight: 700,
                    border: `1px solid ${statusColor}40`,
                  }}
                />
                {sub.status === 'approved' && sub.approvalAmount && (
                  <Chip label={currency(sub.approvalAmount)} size="small"
                    sx={{ bgcolor: '#10b98118', color: '#10b981', fontWeight: 700 }} />
                )}
                {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </Stack>

              {/* Expanded details */}
              <Collapse in={isExpanded}>
                <Box sx={{ px: 2, pb: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                  {sub.status === 'approved' && (
                    <Stack gap={0.5} mt={1}>
                      <Typography variant="caption" color="text.secondary">
                        Approval Amount: <strong style={{ color: '#10b981' }}>{currency(sub.approvalAmount)}</strong>
                      </Typography>
                      {sub.approvalTerm && (
                        <Typography variant="caption" color="text.secondary">
                          Term: <strong>{sub.approvalTerm}</strong>
                        </Typography>
                      )}
                    </Stack>
                  )}

                  {sub.status === 'declined' && sub.declineNotes && (
                    <Box mt={1}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>Decline Reason:</Typography>
                      <Typography variant="body2" sx={{ mt: 0.25, color: '#ef4444', whiteSpace: 'pre-wrap' }}>
                        {sub.declineNotes}
                      </Typography>
                    </Box>
                  )}

                  {sub.status === 'pending' && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Awaiting response from {sub.bankName}
                    </Typography>
                  )}

                  <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
                    Added by {sub.createdByName || 'System'} · {fmtDate(sub.createdAt)}
                  </Typography>

                  <Stack direction="row" gap={1} mt={1}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleEdit(sub)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDelete(sub._id)}>
                        <DeleteIcon fontSize="small" sx={{ color: 'error.light' }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              </Collapse>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
}
